// Phase 70 / 71: single source of truth for "what flow is this template
// running?" and "which upsell defines the design step / sleeve cross-
// sell?". Reads everything from `config.flowBlueprint` (a row in the
// FlowBlueprint Prisma table), populated by the admin and emitted on
// the resolved config from /api/builder/config.
//
// The legacy `Template.customiserFlow` column was dropped in Phase 71
// after an audit script (scripts/audit-flow-blueprint-binding.ts)
// confirmed every Template had `flowBlueprintId` set. Before Phase 71,
// these helpers fell back to the column + magic Upsell.kind strings
// (`pet_portrait`, `kid_drawing`, etc.) for resilience during the
// migration window. That fallback is gone.
//
// When a config arrives without a blueprint (legacy ProductConfig
// fallback path - a product with no matched Template), the customiser
// defaults to the standard logo flow with no design upsell.

import type { Config, Upsell } from "./types";

export type FlowKind = "logo" | "pet" | "kid" | "person";

/** Read the active flow kind. Defaults to "logo" when no blueprint is
 *  attached (legacy ProductConfig fallback path with no matched
 *  Template). */
export function flowKindFor(config: Config | null | undefined): FlowKind {
  if (!config) return "logo";
  const kind = config.flowBlueprint?.kind;
  if (kind === "logo" || kind === "pet" || kind === "kid" || kind === "person") {
    return kind;
  }
  return "logo";
}

/** Resolve the upsell that defines the "design input" step (pet
 *  portrait scan, kid drawing scan, etc.) for the active flow.
 *  Null on the logo flow or when the blueprint hasn't wired a design
 *  upsell. */
export function designUpsellFor(config: Config | null | undefined): Upsell | null {
  if (!config) return null;
  const id = config.flowBlueprint?.designUpsellId;
  if (!id) return null;
  const upsells = config.upsells ?? [];
  return upsells.find((u) => u.id === id) ?? null;
}

/** Resolve the sleeve cross-sell upsell. Returns null when the
 *  blueprint disables sleeve cross-sell or no matching upsell exists.
 *
 *  `preferKidVariant` (true when the customer has at least one saved
 *  kid-drawing spot) bumps the blueprint's default to the kid-specific
 *  `sleeve_kid_name_age` variant when present. This handles the
 *  templates that ship with the standard "sleeve_name" upsell on their
 *  blueprint but want the name+age copy on kid-flow products. */
export function sleeveCrossSellUpsellFor(
  config: Config | null | undefined,
  preferKidVariant: boolean = false,
): Upsell | null {
  if (!config) return null;
  const upsells = config.upsells ?? [];
  const id = config.flowBlueprint?.sleeveCrossSellUpsellId;
  if (!id) return null;
  const exact = upsells.find((u) => u.id === id) ?? null;
  if (!preferKidVariant) return exact;
  // Kid-name+age variant wins when present, regardless of the
  // blueprint's default. Falls back to the blueprint's pick if no
  // kid-specific variant is configured.
  return (
    upsells.find((u) => u.kind === "sleeve_kid_name_age") ?? exact
  );
}

/** True when the active flow shows a dedicated single-design step
 *  (upload + optional text). Pet, kid, and person all match. */
export function isSingleDesignFlow(config: Config | null | undefined): boolean {
  const k = flowKindFor(config);
  return k === "pet" || k === "kid" || k === "person";
}

/** Pet-photo trap (2026-07-12): resolve the LOGO flow's in-place
 *  "Upgrade to Portrait" upsell. Null on non-logo flows or when the
 *  blueprint hasn't wired a photo-upgrade upsell. The resolver already
 *  guarantees the same upsell object also rides in config.upsells, so
 *  pricing.ts + propertiesFor find it by id like any scan upsell. */
export function photoUpgradeUpsellFor(
  config: Config | null | undefined,
): Upsell | null {
  if (!config) return null;
  if (flowKindFor(config) !== "logo") return null;
  return config.photoUpgrade ?? null;
}
